// src/services/authFetch.js
// Prefer explicit API base; in development default to Laravel at 127.0.0.1:8000
const API_BASE_URL = (() => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase) return envBase.replace(/\/$/, '');
  if (process.env.NODE_ENV === 'development') return 'http://127.0.0.1:8000';
  return (window.location.origin || '').replace(/\/$/, '');
})();

export async function authFetch(url, options = {}) {
  const token = localStorage.getItem('token');
  
  // Allow callers to control auto-logout behavior on 401/405
  const { autoLogoutOn401 = true, ...fetchOptions } = options;
  
  // Handle headers properly for FormData vs JSON
  const headers = {
    'Authorization': `Bearer ${token}`,
    ...(fetchOptions.headers || {})
  };
  
  // Don't set Content-Type for FormData - let browser handle it
  if (!(fetchOptions.body instanceof FormData)) {
    headers['Accept'] = 'application/json';
    if (!headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
  } else {
    // For FormData, only set Accept header
    headers['Accept'] = 'application/json';
    // Remove Content-Type to let browser set multipart/form-data with boundary
    delete headers['Content-Type'];
  }

  // Prepend base URL if the URL doesn't start with http
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
  
  // Default timeout for mobile compatibility
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    if (!fetchOptions.signal) { // Only abort if no custom signal provided
      controller.abort();
    }
  }, 120000); // 2 minute default timeout
  
  try {
    const response = await fetch(fullUrl, { 
      ...fetchOptions, 
      headers,
      signal: fetchOptions.signal || controller.signal
    });
    
    clearTimeout(timeoutId);

    if (response.status === 401 || response.status === 405) {
      if (autoLogoutOn401) {
        // Clear all authentication data and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('userRole');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(new Error('Unauthorized or session expired. Redirecting to login.'));
      } else {
        // Do NOT auto-logout; bubble the error to caller
        return Promise.reject(new Error(`Unauthorized (${response.status}).`));
      }
    }

    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    
    // Enhanced error handling for mobile
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please check your connection and try again.');
    } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      throw new Error('Network connection failed. Please check your internet connection.');
    } else {
      throw error;
    }
  }
}
